<template>
    <m-slide-up :show="show" @close="() => $emit('close')">
     
       <div class="px-7 space-y-4 pb-20  max-h-[95vh] overflow-y-auto">
            <!-- <div class="">
                <pre>{{ JSON.stringify(user, null, 2) }}</pre>
            </div> -->

            <h1 class="font-bold text-lg">{{ user ? 'Update User' : 'Create User' }}</h1>

            <m-form-group label="Username">
                <input autocapitalize="none" v-model="newUser.username" type="text" class="form-control" placeholder="username" />
            </m-form-group>

            <m-form-group label="Email">
                <input :readonly="user ? true : false" autocapitalize="none" v-model="newUser.email" type="text" class="form-control" placeholder="email@email.com" />
            </m-form-group>

            <div class="grid grid-cols-2 gap-3">
                <m-form-group label="First Name">
                    <input autocapitalize="none" v-model="newUser.first_name" type="text" class="form-control" placeholder="First Name" />
                </m-form-group>

                <m-form-group label="Last Name">
                    <input autocapitalize="none" v-model="newUser.last_name" type="text" class="form-control" placeholder="Last Name" />
                </m-form-group>
            </div>

            <m-form-group label="Assigned wards">
                 <m-search-pop-list
                    @change="(data) => addWards(data.value)"
                    @input="(text) => searchWardString = text" :searchList="filteredWards"
                    searchWrapperClass="border" inputClass="w-full" class="w-full border mb-3"
                    placeholder="Search ward" />

                <div class="flex items-center flex-wrap gap-2">
                    <div class="border rounded-3xl flex items-cente py-1 px-3" v-for="(ward, n) in selectedWards" :key="n">{{ ward?.header?.trim?.() }} <button @click="() => removeWard(ward)"><church-icon class="!w-4 !h-4" icon="x-mark" /></button></div>
                </div>
               
            </m-form-group>

            <m-form-group label="Origanization">
                <m-search-pop-list
                    :value="selectedOrganization"
                    @change="(data) => setSelectedOrganization(data.value)"
                    @input="(text) => searchOrganizationString = text" 
                    :searchList="filteredOrganizations"
                    searchWrapperClass="border" inputClass="w-full" class="w-full border mb-3"
                    placeholder="Search organization" />
            </m-form-group>

            <m-form-group label="Position">
                <m-dropdown :value="selectedPosition" :options="modifiedPositions" @change="(data) => newUser.stake_position = data.value" />
            </m-form-group>

            <button :disabled="disableCreateBtn" class="btn-secondary mt-5 w-full flex items-center justify-center" @click="() => createUser()">
                <span v-if="creating" class="loader"></span>
                <span v-else>{{user ? 'Update User' : 'Create User'}}</span>
            </button>

            <a href="#" @click.prevent="() => emit('changePassword')" class="text-center block text-blue">Change password</a>

       </div>
    </m-slide-up>
</template>


<script setup>
import UserApi from "@/apis/userApi";
import WardApis from "@/apis/wardApis";
import StringParser from "@/helpers/stringParser";
import { computed, onBeforeMount, ref, defineProps, defineEmits, watch } from "vue";

/* ======= PROPS ======== */
const props = defineProps({
    show: { type: Boolean, default: false },
    user : { type: [Object,  null], default: () => null }
});

/* ============ EMITS ========= */
const emit = defineEmits(['close', 'change', 'changePassword']);


/* ====== STATES====== */
const defaultUser = {
    username: '',
    email: '',
    first_name: '',
    last_name: '',
    assign_ward: [],
    assign_organization: null,
    stake_position: ''
};
const wards = ref([]);
const organizations = ref([]);
const selectedWards = ref([]);
const searchWardString = ref('');
const newUser = ref({ ...defaultUser });
const creating = ref(false);
const positions = ref([]);
const searchOrganizationString = ref('');

/* ========== COMPUTED ========= */
/**
 * Filtered wards based on search string
 */
const filteredWards = computed(() => {
    if(!searchWardString.value) return []
    return wards.value.filter(ward => ward?.header?.toLowerCase()?.includes?.(searchWardString.value.toLowerCase())).map(ward => {  
        return {
            value: ward,
            label: ward?.header?.trim?.() || 'No name',
            site_domain: ward.site_domain
        };
    });
});

/**
 * Filtered organizations based on search string
 * 
 */
const filteredOrganizations = computed(() => {
    if(!searchOrganizationString.value) return []
    return organizations.value.filter(org => org?.name?.toLowerCase()?.includes?.(searchOrganizationString.value.toLowerCase())).map(org => {  
        return {
            value: org.id,
            label: org?.name?.trim?.() || 'No name',
        };
    });
});
/**
 * Disable create button if creating or required fields are empty
 */
const disableCreateBtn = computed(() => {
    if(creating.value) return true;
    if(!newUser.value.username) return true;
    if(!newUser.value.email) return true;
    if(!newUser.value.first_name) return true;
    if(!newUser.value.last_name) return true;
    if(newUser.value.assign_ward.length === 0) return true;
    if(!newUser.value.stake_position) return true;
    
    return false
});

/**
 * Get selected position label
 */
const selectedPosition = computed(() => {
    return positions.value.find(pos => pos.position_name === newUser.value.stake_position)?.position_name || '';
});

/**
 * Selected organization label
 */
const selectedOrganization = computed(() => {
    const org = organizations.value.find(o => o.id === Number(newUser.value.assign_organization));
    return org ? org.name : '';
});

/**
 * Modified position list
 */
const modifiedPositions = computed(() => 
{
    return positions.value.map(pos => {
        return {
            value : pos.position_name,
            label : pos.position_name,
        }
    })
});
/* ============ METHODS ======== */
/**
 * Add selected ward to the list
 * @param {Object} ward - Ward object to be added
 */
const addWards = (ward) => {
    if (!selectedWards.value.some(w => w.site_domain === ward.site_domain)) {
        selectedWards.value.push(ward);
        newUser.value.assign_ward.push(ward.site_domain);
    }
};

/**
 * Remove ward from the selected list
 * @param {Object} ward - Ward object to be removed
 */
const removeWard = (ward) => {
    selectedWards.value = selectedWards.value.filter(w => w.site_domain !== ward.site_domain);
    newUser.value.assign_ward = newUser.value.assign_ward.filter(w => w !== ward.site_domain);
}

/**
 * Create new user
 */
const createUser = () => {
    if (creating.value) return;
    creating.value = true;


    /**
     * Check if user prop is provided
     * If user prop is provided, it means we are updating an existing user
     */
    if(props.user) {
        const params = {
            ...newUser.value,
            user_id: props.user.id // Ensure we are updating the correct user
        }
        console.log('params', props.user);
        if(params.id) delete params.id; // Remove id if exists to avoid sending it in update request
        if(params.email) delete params.email; // Remove email if exists to avoid sending it in update request
        UserApi.updateUser({
            params ,
            callback : (res) => {
                creating.value = false;
                if(res ) (emit('change', res), emit('close'));
                else creating.value = false;
            },
            errCallback : () => creating.value = false
        })

        return;
    }
    // If user prop is not provided, we are creating a new user
    UserApi.createUser({
        params  : newUser.value,
        callback : (res) => {
            creating.value = false;
            if(res ) (emit('change', res), emit('close'));
            else creating.value = false;
        },
        errCallback : () => creating.value = false
    })

}


/**
 * Set selected organization and get organization positions
 * @param {Number} organization_id - Organization ID
 */
const setSelectedOrganization = (organization_id) => {
    newUser.value.assign_organization = organization_id;
    UserApi.getOrganizationPositions({query: {organization_id}, callback: data => positions.value = data || []});
};

/* ======== Watcher ====== */
/**
 * Watch user prop if provided set the newUser state
 */
watch(() => props.user, (user) => {
    if (user) {
        newUser.value = { ...user };

        /**
         * Get organization positions if organization is set
         */
        if (user.assign_organization) {
            UserApi.getOrganizationPositions({query: {organization_id: user.assign_organization}, callback: data => positions.value = data || []});
        }

        /**
         * Set selected wards based on user data
         */
        const userWards = Array.isArray(user.assign_ward) ? user.assign_ward  : StringParser.stringToJson(user.assign_ward) || [];
        console.log('userWards', userWards, wards.value);
        selectedWards.value = userWards.map(ward => {
            const foundWard = wards.value.find(w => w.site_domain === ward);
            return foundWard ? { ...foundWard, header: foundWard.header.trim() } : null;
        }).filter(w => w !== null);
    }
}, {deep: true});


/**
 * Watch show prop to reset newUser state when closing the pop-up
 */
watch(() => props.show, (show) => {
    if (!show) {
        newUser.value = { ...defaultUser };
        selectedWards.value = [];
        searchWardString.value = '';
        searchOrganizationString.value = '';
        positions.value = [];
    }
}, { immediate: true, deep: true });

/* =========== ONLOAD ====== */
onBeforeMount(() => {
    // Fetch wards from API or store
    WardApis.getAllWards({callback : data => wards.value = data.wards.value || []})
    // Fetch organizations from API or store
    UserApi.getOrganizations({callback : data => organizations.value = data || []});
});


</script>